/* eslint-disable react/prop-types */
/* eslint-disable keyword-spacing */
import React, { Component } from 'react';

import ErrorBoundaryCases from './error-boundary.config';
import {
    categorizeErrorBoundaryError,
    getErrorBoundaryRoute,
} from './error-boundary-telemetry';
import { trackEvent } from '../../utils/analytics';
import ANALYTIC_EVENTS from '../../constants/analytic-event-constants';
import { LOG_EVENT_TYPES, logger } from 'src/logger/logger';

/** Widget/chart fallbacks — anything else is treated as full-page Error UI. */
const WIDGET_ERROR_FALLBACKS = new Set([
    'chart-has-an-error',
    'traffic-lights-error',
    'critical-alerts-error',
    'overall-performance-error',
    'route-details-has-error',
]);

const resolveComponentName = (children) => {
    try {
        const type = children?.type;
        return type?.displayName || type?.name || 'UnknownComponent';
    } catch {
        return 'UnknownComponent';
    }
};

/**
 * Thin React error boundary.
 *
 * Responsibilities only:
 * 1. Catch render errors and show a generic fallback
 * 2. Emit a GA/Clarity analytics event (allowlisted error category only)
 * 3. Emit an ops logger beacon for RUM (pathname + category only)
 *
 * Do not dig Redux or chart-specific context here — that work can throw and
 * defeat the boundary. Session context (e.g. agency_id) belongs on logger
 * context / RUM session attributes set upstream.
 * Do not render raw error text to users — keep messaging generic.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            errorMessage: null,
            errorName: null,
        };
    }

    static getDerivedStateFromError(error) {
        return {
            errorMessage: error?.message || 'Unknown error',
            errorName: error?.name || 'Error',
        };
    }

    componentDidCatch(error) {
        const componentName = resolveComponentName(this.props.children);
        const fallBackType = this.props.fallBack || 'unknown';
        const errorCategory = categorizeErrorBoundaryError(error);
        const isFullPageFallback = !WIDGET_ERROR_FALLBACKS.has(this.props.fallBack);

        // Reporting must never throw — wrap the entire side-effect block.
        try {
            trackEvent(ANALYTIC_EVENTS.DASHBOARD_ERROR_BOUNDARY_TRIGGERED, {
                component: componentName,
                fallback_type: fallBackType,
                error_category: errorCategory,
            });

            logger.event(LOG_EVENT_TYPES.ERROR_BOUNDARY, {
                message: 'ErrorBoundary rendered fallback',
                fallback_type: fallBackType,
                component: componentName,
                route: getErrorBoundaryRoute(),
                error_category: errorCategory,
                is_full_page_fallback: isFullPageFallback,
            });
        } catch (reportingError) {
            // Boundary must remain usable even if analytics/logger fail.
        }
    }

    render() {
        if (this.state.errorMessage) {
            return (
                <ErrorBoundaryCases
                    errorCase={this.props.fallBack}
                    errorComponent={resolveComponentName(this.props.children)}
                />
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
